import { removeHashFromUrl } from 'Utils/url';

export function getScrollTop(container = window) {
  if (container === window) return container.pageYOffset || document.body.scrollTop;
  return container.scrollTop;
}

export function scrollToTop(scrollDuration = 0, _container) {
  const container = _container || document.documentElement;
  const cosParameter = container.scrollTop / 2;
  let scrollCount = 0;
  let oldTimestamp = performance.now();

  function step(newTimestamp) {
    scrollCount += Math.PI / (scrollDuration / (newTimestamp - oldTimestamp));
    if (scrollCount >= Math.PI) container.scrollTo(0, 0);
    if (container.scrollTop === 0) return;
    container.scrollTo(0, Math.round(cosParameter + (cosParameter * Math.cos(scrollCount))));
    oldTimestamp = newTimestamp;
    requestAnimationFrame(step);
  }
  requestAnimationFrame(step);
}

function getElementCoords(el) { // crossbrowser version
  const box = el.getBoundingClientRect();
  const { body } = document;
  const docEl = document.documentElement;

  const scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop;
  const scrollLeft = window.pageXOffset || docEl.scrollLeft || body.scrollLeft;

  const clientTop = docEl.clientTop || body.clientTop || 0;
  const clientLeft = docEl.clientLeft || body.clientLeft || 0;

  const top = (box.top + scrollTop) - clientTop;
  const left = (box.left + scrollLeft) - clientLeft;

  return { top: Math.round(top), left: Math.round(left) };
}

/**
 * Get max scroll top for given container
 * @param container {element|Window}
 * @returns {number}
 */
export function getMaxScrollTop(container = window) {
  if (container === window) return document.body.offsetHeight - window.innerHeight;
  return container.scrollHeight - container.clientHeight;
}

/**
 * Get normalized Y scroll position
 * if given topOffset great than max scroll, returned max scroll
 * @param topOffset {number}
 * @param container {element|window}
 * @returns {number}
 */
export function getNormalizedScrollTop(topOffset, container = window) {
  const maxScroll = getMaxScrollTop(container);
  return (topOffset > maxScroll) ? maxScroll : topOffset;
}

/**
 * Call callback function on scroll complete
 * @param topOffset {Number} - scroll to top offset
 * @param callback {Function} - function to call after scroll
 * @param container {Element|Window} - container which will be scrolled
 */
function onScrollAchieved(topOffset, callback, container = window) {
  let timeoutId = null;
  const onScroll = function () {
    if (getScrollTop(container) === topOffset) {
      container.removeEventListener('scroll', onScroll);
      clearTimeout(timeoutId);
      callback();
    }
  };
  // after timeout 3sec detection will be switch off even if scroll position is not reached
  timeoutId = setTimeout(() => {
    container.removeEventListener('scroll', onScroll);
  }, 3000);
  container.addEventListener('scroll', onScroll);
  onScroll();
}

/**
 * Call passed callback, when scroll position is changed to different than passed
 * @param topOffset
 * @param callback
 * @param container
 */
function onScrollLeave(topOffset, callback, container = window) {
  const onScroll = function () {
    if (getScrollTop(container) !== topOffset) {
      callback();
      container.removeEventListener('scroll', onScroll);
    }
  };
  container.addEventListener('scroll', onScroll);
}

/**
 * Get element coordinates related to modal window
 * @param el
 * @param modalEl
 * @returns {{top: *, left: *}}
 */
function getElementCoordsInModal(el, modalEl) {
  const elCoords = el.getBoundingClientRect();
  const modalBox = modalEl.getBoundingClientRect();
  const { scrollTop, scrollLeft, clientTop = 0, clientLeft = 0 } = modalEl;
  const top = Math.round(elCoords.top + scrollTop - modalBox.top - clientTop);
  const left = Math.round(elCoords.left + scrollLeft - modalBox.left - clientLeft);
  return { top, left };
}

/**
 * Scroll to top of passed element
 * @param el - element to scroll top position
 * @param offsetTop {number} - value add to top element position
 * @param _container {element|Window} - container to scroll
 * @param callback {Function} - callback on scroll complete
 */
export function scrollToElement(el, offsetTop = 0, _container, callback) {
  const container = _container || window;
  // Get coordinates according to scroll window.
  const { top } = container.classList && container.classList.contains('v-dialog')
    ? getElementCoordsInModal(el, container)
    : getElementCoords(el);
  const scrollToOffset = getNormalizedScrollTop(top + offsetTop, container);
  // if callback passed, call it on scroll complete
  if (callback) {
    onScrollAchieved(scrollToOffset, callback, container);
  }
  (container || window).scrollTo({
    top: scrollToOffset,
    behavior: 'smooth',
  });
  return { scrollToOffset };
}

/**
 * Scroll to anchor by hash
 * if hash exist in url, function wait for scroll complete
 * detect if scroll leave position and then remove hash from url
 * @param hash
 * @param offsetTop
 * @param container
 */
export function scrollToAnchor(hash, offsetTop, container = window) {
  if (!hash) return;
  // normalize hash
  const h = hash.startsWith('#') ? hash : `#${hash}`;
  try {
    // get element by id selector
    const el = (container.querySelector ? container : document).querySelector(h);
    // if element exist
    if (el) {
      // call scrollToElement func and pass callback to remove hash from url on scroll position leave
      const { scrollToOffset } = scrollToElement(el, offsetTop, container, () => {
        onScrollLeave(scrollToOffset, () => removeHashFromUrl(hash), container);
      });
    }
  } catch (ignore) {} // eslint-disable-line
}

/**
 * Check if element is visible at window box
 * @param el
 * @param offsetTop
 * @returns {boolean}
 */
export function isElementVisible(el, offsetTop = 0) {
  const rect = el.getBoundingClientRect();
  const elTop = rect.top;
  const elBottom = rect.bottom;

  // Only completely visible elements return true:
  return (elTop >= offsetTop) && (elBottom <= window.innerHeight);
  // Partially visible elements return true:
  // isVisible = elemTop < window.innerHeight && elemBottom >= 0;
}

export function scrollToElementIfNotVisible(el, offsetTop = 0) {
  if (!isElementVisible(el, offsetTop * -1)) {
    scrollToElement(el, offsetTop);
  }
}

export function getIsTabletOrMobileByUserAgent() {
  const userAgent = navigator.userAgent.toLowerCase();
  return Boolean(/(ipad|tablet|iphone|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));
}
